/**
 * ATL-1381 — the `previousInStock` a stock movement is written against.
 *
 * ## What `previousInStock` means
 *
 * Every movement document records the stock the operation was applied TO, so that consecutive
 * movements on the same (product, store) form a closed chain:
 *
 *     next.previousInStock === current.previousInStock + current.increment
 *
 * That rule is not decorative. `validateMovementsWithIncrements` (utils/validationFunctions.js)
 * evaluates exactly it, and `ProductStockMovementScreen` renders a row that fails it in red as
 * «Ошибка, возможно нужен пересчет предыдущей операции» — the merchant-facing signal that stock
 * and its ledger have diverged. A baseline that breaks the chain for a benign reason therefore
 * costs more than a wrong number: it spends the one alarm the product has.
 *
 * ## The defect this exists to remove
 *
 * Every write path read the baseline straight out of the products dict, once per cart line:
 *
 *     previousInStock: allProductsDict[value.id]?.storeStock?.[info.store] || 0
 *
 * The dict is a read model fed by a Firestore listener; nothing advances it while the batch is
 * being built. So when ONE operation touches the same product on more than one line, every one of
 * those lines records the SAME baseline while each applies its own `increment` — and the chain
 * breaks by the earlier line's increment, permanently, on a sale where nothing at all went wrong.
 *
 * That is not an exotic input. `mergeSimilarItems` off makes every tap its own line (see the
 * docblock on utils/outOfStockGuard.ts, which sums per product across lines for the same reason),
 * a modified line forces the next tap into a new one regardless of the setting, and two composite
 * lines routinely consume the same ingredient.
 *
 * The running value is also what the OTHER store has always written, which is evidence rather than
 * assertion that it is the intended semantics: `create_sale_atomic` re-reads
 * `product_store_stock` per update inside its own transaction and stamps THAT into
 * `product_movements.previous_stock` (create_sale_atomic.sql:130-146). Firestore was the odd one
 * out. Note the backend does not consume the client's `previousInStock` at all, so this fix is
 * Firestore-only by construction and moves no dual-write payload.
 *
 * ## A running baseline is only half of it: the reader has to be able to see the order
 *
 * Every movement in one batch resolves `fieldValue.serverTimestamp()` to the SAME commit
 * timestamp, and `getProductMovementOnDateRange` orders by `date` alone. Firestore then breaks the
 * tie on `__name__` — the random auto-ids `.doc()` minted — so a correctly chained pair comes back
 * in write order only about half the time, and `validateMovementsWithIncrements` raises the alarm
 * on the other half. Advancing the baseline without fixing that would have halved the false alarm
 * and been documented as removing it.
 *
 * Hence `writeSeq`: each row carries its index within the operation that wrote it, and
 * `sortMovementsForChain` (below, applied by `getProductMovementOnDateRange`) uses it as a
 * secondary sort on the READ side. It is a
 * client-side sort on purpose — an `orderBy('writeSeq')` would EXCLUDE every movement written
 * before this field existed, which is the whole history. Rows without it sort as 0 and a stable
 * sort leaves their relative order exactly as it is today.
 *
 * ## What it does NOT fix, stated here so the next reader does not have to re-derive it
 *
 * It does not make the stock write itself idempotent, and the residue ATL-1381 was reported for is
 * NOT this defect — that one is open as ATL-1382. Firestore delivers mutations at least once: when
 * the write stream breaks after the server committed but before the ack arrives, the SDK re-sends the batch, the
 * `increment` transform applies AGAIN, and `batch.set(movementRef, …)` lands on the same
 * client-minted id, so exactly one movement row survives claiming a single-quantity move. Stock
 * ends up short by the line's quantity with a ledger that cannot show it. `deleteSale` is guarded
 * against the mirror image of that (runIdempotentDelete, ATL-670/ATL-779); `saveNewSale` cannot use
 * a transaction — they fail when the client is offline and the checkout is built to keep selling
 * offline. Since ATL-2004 it is guarded by a pre-acknowledged write claim instead (`writeClaims.ts`:
 * the batch also updates+deletes a claim document minted and acknowledged earlier, so a re-delivery
 * is refused atomically). That guard ships dark behind Remote Config `write_claim_org_allowlist`,
 * and a sale committed without a claim (org not allowlisted, empty pool) still double-applies
 * exactly as described here.
 *
 * It is also scoped to `saveNewSale` and `deleteSale`. The same per-line dict re-read lives in the
 * return, supply, write-off, transfer and manufacture paths (firebase.ts, inventory.ts) and is
 * tracked as ATL-1383 — do not read the registry's note on this operation as a claim about those.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- the products dict and cart lines are `any` throughout this module */

/**
 * The chain fields of ONE movement row.
 *
 * Assign them by name at the call site rather than spreading this object into the payload: the
 * ATL-1041 unbounded-write census reads the payload's key set out of the SOURCE, and a spread
 * makes a write's field set invisible to it (four of these turned four bounded movement writes
 * into unbounded ones and tripped its ratchet, which is the census working as intended).
 */
export interface MovementChainFields {
  /** The stock this row's `increment` was applied to. */
  previousInStock: number
  /**
   * This row's index within the ONE operation that wrote it — the only deterministic tiebreak
   * available to a reader for rows that share a commit timestamp. See the docblock above.
   */
  writeSeq: number
}

export interface StockBaseline {
  /**
   * The chain fields for ONE movement row, advancing the running value by `increment`.
   *
   * Call it exactly once per movement document written, in the order the increments are applied.
   */
  next(productId: string, increment: number): MovementChainFields
}

/**
 * Track per-product stock across the lines of a single operation.
 *
 * Seeded from the products dict (`|| 0` when the product is absent from it, which is the
 * pre-existing behaviour at every call site and is deliberately unchanged here: a catalog-removed
 * product has no trustworthy baseline, and inventing one from the cart line's frozen snapshot
 * would be worse than recording the zero the reader can recognise).
 *
 * MUST be constructed inside any retryable callback that uses it — a Firestore transaction body
 * may run more than once, and a tracker built outside would carry the abandoned attempt's
 * advanced values into the next one.
 */
export const stockBaselineTracker = (
  productsDict: Record<string, any>,
  store: string,
): StockBaseline => {
  const running = new Map<string, number>()
  let writeSeq = 0
  return {
    next(productId: string, increment: number): MovementChainFields {
      const previous = running.has(productId)
        ? (running.get(productId) as number)
        : productsDict[productId]?.storeStock?.[store] || 0
      running.set(productId, previous + increment)
      return { previousInStock: previous, writeSeq: writeSeq++ }
    },
  }
}

/**
 * The only two fields the chain ordering reads. Structural on purpose: this module must stay free
 * of Firestore imports (products.ts imports IT), and both `ProductMovement` declarations fit.
 */
export interface ChainOrderable {
  date?: unknown
  writeSeq?: number
}

/**
 * Put movements that share a commit timestamp back into the order they were written (ATL-1381).
 *
 * Every movement written by one `WriteBatch` or transaction resolves `serverTimestamp()` to the
 * SAME commit timestamp, and the query below can only order by `date`. Firestore breaks that tie
 * on `__name__` — the random auto-ids `.doc()` minted — so a sale that touched one product on two
 * lines comes back in write order about half the time. `validateMovementsWithIncrements` checks
 * `next.previousInStock === cur.previousInStock + cur.increment` against whatever order it is
 * handed, so the other half renders red as «Ошибка, возможно нужен пересчет предыдущей операции»
 * on an operation where nothing is wrong.
 *
 * `writeSeq` (stockBaseline.ts) is that row's index within the operation that wrote it. Applying
 * it HERE rather than as an `orderBy` is deliberate: Firestore excludes documents that lack the
 * ordered field, so an `orderBy('writeSeq')` would hide every movement written before the field
 * existed — the entire history. Absent values sort as 0 and `Array.prototype.sort` is stable, so
 * rows without it keep exactly the order they have today.
 *
 * Input is expected in `date` DESC order (newest first), which is what both read paths produce.
 *
 * A tie means the SAME instant to the nanosecond, which in practice means one commit.
 */
export const sortMovementsForChain = <T extends ChainOrderable>(movements: readonly T[]): T[] => {
  // Compared as (seconds, nanoseconds) rather than milliseconds. Firestore stores and ORDERS BY
  // the full-precision timestamp, so truncating to `getTime()` would merge two DIFFERENT commits
  // that landed inside the same millisecond into a tie — and then decide it with `writeSeq`, which
  // only means anything within ONE operation. That re-orders a pair the query had already handed
  // back correctly and re-introduces the false alarm this exists to remove (reachable on an
  // offline-queue flush, or two tills on one product). Not folded into one number on purpose:
  // seconds × 1e9 exceeds `Number.MAX_SAFE_INTEGER`, so the fold would lose the precision it is
  // being added to preserve.
  const instant = (m: ChainOrderable): { seconds: number; nanos: number } => {
    const raw = m.date as
      | { seconds?: number; nanoseconds?: number; toDate?: () => Date }
      | string
      | Date
      | undefined
    if (raw && typeof raw === 'object') {
      const { seconds, nanoseconds } = raw as { seconds?: number; nanoseconds?: number }
      if (typeof seconds === 'number' && typeof nanoseconds === 'number') {
        return { seconds, nanos: nanoseconds }
      }
      const toDate = (raw as { toDate?: () => Date }).toDate
      if (typeof toDate === 'function') return fromMillis(toDate.call(raw).getTime())
    }
    return fromMillis(new Date(raw as string | Date).getTime())
  }
  const fromMillis = (ms: number): { seconds: number; nanos: number } => {
    if (!Number.isFinite(ms)) return { seconds: 0, nanos: 0 }
    return { seconds: Math.floor(ms / 1000), nanos: (((ms % 1000) + 1000) % 1000) * 1e6 }
  }
  return [...movements].sort((a, b) => {
    const left = instant(a)
    const right = instant(b)
    if (right.seconds !== left.seconds) return right.seconds - left.seconds
    if (right.nanos !== left.nanos) return right.nanos - left.nanos
    return (b.writeSeq ?? 0) - (a.writeSeq ?? 0)
  })
}
